import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

import {
    AppNotificationService,
    AppNotificationType,
    NOTIFICATION_STATUS_CODE,
    SynchronizationService,
    TravelStatusUpdateModel,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, concatMap, exhaustMap, take, takeUntil, tap } from 'rxjs/operators';
import { TravelStatusUpdateService } from './travel-status-update.service';

@Component({
    selector: 'pace-travel-status-update',
    templateUrl: './travel-status-update.component.html',
    styleUrls: ['./travel-status-update.component.scss'],
})
export class TravelStatusUpdateComponent implements OnInit, OnDestroy {
    @Input()
    public model: TravelStatusUpdateModel;

    @Output()
    public statusSubmitted = new EventEmitter<NOTIFICATION_STATUS_CODE>();

    public statusText: string;
    public isLoading = false;

    public formChildren: FormArray;
    public travelStatusForm: FormGroup;

    private submitStatusSubject: Subject<void>;
    private destroy$ = new Subject<void>();

    private submitStatusChanged$: Observable<void>;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly travelStatusUpdateService: TravelStatusUpdateService,
        private readonly synchronizationService: SynchronizationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
    ) {
        this.submitStatusSubject = new Subject<void>();
        this.submitStatusChanged$ = this.submitStatusSubject.asObservable();
    }

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.travelStatusForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        this.submitStatusChanged$
            .pipe(
                exhaustMap(() =>
                    this.travelStatusUpdateService.submitStatus(this.model).pipe(
                        concatMap((travelStatusModel) => {
                            // emit event that the travel status was updated to reload the status stepper button model
                            this.statusSubmitted.emit(travelStatusModel.nextStatus);
                            this.modalInstance.hide();
                            this.log.debug(`TravelStatusUpdateComponent: submitStatus: sendOfflineData`);
                            return this.sendOfflineData();
                        }),
                        tap({
                            error: (error) => {
                                this.log.debug(
                                    `TravelStatusUpdateComponent: submitStatus: error: ${JSON.stringify(
                                        error,
                                        Object.getOwnPropertyNames(error),
                                    )}`,
                                );
                                this.modalInstance.hide();
                            },
                        }),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
    }

    public submitStatus(): void {
        this.isLoading = true;
        this.triggerSubmitStatusChanged();
    }

    public triggerSubmitStatusChanged(): void {
        this.submitStatusSubject.next();
    }

    // Synchronize the offline data
    private sendOfflineData(): Observable<void> {
        return this.synchronizationService.sendOfflineData().pipe(
            take(1),
            catchError((error) => {
                this.log.error(
                    `TravelStatusUpdateComponent: sendOfflineData: error:
                                ${JSON.stringify(error, Object.getOwnPropertyNames(error))}`,
                );
                this.appNotificationService.setAppNotification(
                    AppNotificationType.WARN,
                    <string>this.translate.instant('APP_SYNC_FAILED_TITLE'),
                    <string>this.translate.instant('APP_SYNC_FAILED_SYNCHRONIZATION'),
                );
                return EMPTY;
            }),
        );
    }

    public updateValidityAndEndMoment(): void {
        this.formChildren.controls.forEach((control) => {
            control.updateValueAndValidity();
        });
        this.model.updateEndMoment();
    }
}
